// AppShellHost context — the typed bundle the composition root (AppShellHost)
// assembles once and every presentational sub-component reads.
//
// This is the ui-as-projection seam for the shell's render tree
// (docs/implement/ui-as-projection.md): the impure shell owns all state,
// effects and port calls; the view components below the provider stay pure and
// take every fact they render + every intent they emit off THIS bundle instead
// of closing over the shell's locals. Behaviour-identical to the former
// closures — the same values, read at the same render moment — but the render
// tree no longer lives inside the 3.5k-line host.
//
// The context carries no default: the host always provides a real model, and
// `useAppShell` throws a loud wiring-bug error if a view is mounted outside the
// provider (the same discipline as `useAppPorts`).
//
// Field typing convention: hook-bundle intents/data are `Pick`ed off the
// producing hook's return type (no re-declaration, no extra imports); aggregate
// host APIs are carried whole (editor/navHost/omnibar/coach); everything else is
// a loose state value / derivation / shell handler typed explicitly.

import type * as Core from "@bilda/core";
import {
    createContext,
    type Dispatch,
    type RefObject,
    type SetStateAction,
    useContext,
} from "react";
import type { SecondarySlot } from "../../components/AppShell/AppShell.js";
import type { ChatScope } from "../../components/Chat/presentation.js";
import type {
    AmbientContextItem,
    SelectableContext,
} from "../../components/Chat/types.js";
import type { CoachHostApi } from "../../components/CoachHost/CoachHost.js";
import type {
    ComposerResourceRef,
    ComposerSlashCommand,
} from "../../components/Composer/PowerComposer/types.js";
import type { EditorHostApi } from "../../components/EditorHost/EditorHost.js";
import type {
    ExportFormat,
    ExportState,
} from "../../components/ExportFlow/ExportFlow.js";
import type { FinderDoc } from "../../components/Finder/finderIndex.js";
import type { HeroHostApi } from "../../components/HeroHost/HeroHost.js";
import type { IngestHostHandle } from "../../components/IngestHost/IngestHost.js";
import type { KbdPlatform } from "../../components/Kbd/Kbd.js";
import type {
    MaterialCreateState,
    NewMaterial,
    TemplateCatalog,
} from "../../components/MaterialCreate/MaterialCreate.js";
import type { NavHostResult } from "../../components/NavHost/NavHost.js";
import type {
    ConversationId,
    ConversationRailItem,
    MaterialRef,
    ResourceId,
    ResourceRef,
    SourceRef,
    TabSet,
} from "../../components/Navigation/types.js";
import type { OmnibarHostApi } from "../../components/OmnibarHost/OmnibarHost.js";
import type { MaterialQuality } from "../../components/QualitySummary/QualitySummary.js";
import type {
    SearchFacet,
    SearchResult,
} from "../../components/Search/searchModel.js";
import type { RecentThread } from "../../components/WorkspaceLanding/WorkspaceLanding.js";
import type { ChatSession } from "../../modules/chat/model.js";
import type {
    ChatCommand,
    ChatIntents,
    ChatState,
    TurnFailure,
} from "../../modules/chat.js";
import type { ConversationList } from "../conversationSummary.js";
import type { ExternalError } from "../externalError.js";
import type {
    dockState,
    FrameState,
    FrameTransition,
    HostNotice,
    OverlaySurface,
    workspaceSurface,
} from "../frame.js";
import type { IngestFile, IngestId } from "../ingest.js";
import type { PanelDragConfig } from "../panelDragOpen.js";
import type { readPanelLayout } from "../panelLayout.js";
import type { PendingGrounding } from "../pendingGrounding.js";
import type {
    AppPorts,
    AuthStatus,
    ExportReady,
    FileHistoryView,
    HistoryView,
    Library,
    LoadedExport,
    LoadedSource,
} from "../ports.js";
import type { MessageControls } from "./MessageLayer.js";
import type { useConnectionHealth } from "./useConnectionHealth.js";
import type { useContextSpine } from "./useContextSpine.js";
import type { LibraryMount } from "./useLibraryModule.js";
import type { LifecycleSurfaces, Renaming } from "./useLifecycleSurfaces.js";
import type { MaterialSideView } from "./useMaterialSideView.js";
import type { NoticeViewDeps } from "./useNoticeView.js";
import type { useObjectNav } from "./useObjectNav.js";
import type { StudioCommands } from "./useStudioCommands.js";

// The shell model — the whole fact + intent surface the render tree projects.
// Every field is a value the host already held; a view reads it here instead of
// closing over the host's local.
export type AppShellModel = {
    // F48 — the 404 gate: `true` when the current URL is unrecognized (a real
    // "page not found"); `onRouteHome` is its one way out (`/` = new chat).
    // A projection of the URL boundary, not a frame factor.
    readonly routeNotFound: boolean;
    readonly onRouteHome: () => void;

    // The one frame product (frame.ts) + its writer. Views destructure the
    // factors they need (openSecondary, overlay, paneTree, anchor, …).
    readonly frame: FrameState;
    readonly dispatch: Dispatch<FrameTransition>;

    // The command/query seam + the four aggregate host APIs.
    readonly ports: AppPorts;
    readonly editor: EditorHostApi;
    readonly navHost: NavHostResult;
    readonly omnibar: OmnibarHostApi;
    readonly coach: CoachHostApi;

    // The generation side view (the `material` module's mount, phase 2c).
    readonly materialSide: MaterialSideView;

    // Object-nav intents.
    readonly openRef: ReturnType<typeof useObjectNav>["openRef"];
    readonly onActivateTab: ReturnType<typeof useObjectNav>["onActivateTab"];
    readonly onFinderJump: ReturnType<typeof useObjectNav>["onFinderJump"];
    readonly goToChange: ReturnType<typeof useObjectNav>["goToChange"];
    readonly onCloseTab: ReturnType<typeof useObjectNav>["onCloseTab"];
    readonly openConversation: ReturnType<
        typeof useObjectNav
    >["openConversation"];
    readonly enterFullChat: ReturnType<typeof useObjectNav>["enterFullChat"];
    readonly applyIngest: ReturnType<typeof useObjectNav>["applyIngest"];

    // Context-spine intents.
    readonly introduceIntoActiveContext: ReturnType<
        typeof useContextSpine
    >["introduceIntoActiveContext"];
    readonly dropFromActiveContext: ReturnType<
        typeof useContextSpine
    >["dropFromActiveContext"];
    readonly clearActiveContext: ReturnType<
        typeof useContextSpine
    >["clearActiveContext"];
    readonly promoteAmbientItem: ReturnType<
        typeof useContextSpine
    >["promoteAmbientItem"];
    readonly handleMentionsChange: ReturnType<
        typeof useContextSpine
    >["handleMentionsChange"];
    readonly groundAddedSource: ReturnType<
        typeof useContextSpine
    >["groundAddedSource"];

    // Chat: the module's door + the intents built on it.
    readonly sendChat: (command: ChatCommand) => void;
    readonly submitText: ChatIntents["submitText"];
    readonly retryTurn: ChatIntents["retryTurn"];
    readonly stopTurn: ChatIntents["stopTurn"];
    readonly acceptOpeningMove: ChatIntents["acceptOpeningMove"];
    readonly draftMaterialFromSource: ChatIntents["draftMaterialFromSource"];
    readonly supplyFormAnswer: ChatIntents["supplyFormAnswer"];

    // Connection health.
    readonly health: ReturnType<typeof useConnectionHealth>["health"];
    readonly lastCheckedAt: ReturnType<
        typeof useConnectionHealth
    >["lastCheckedAt"];
    readonly healthReason: ReturnType<typeof useConnectionHealth>["reason"];
    readonly nextRetryAt: ReturnType<typeof useConnectionHealth>["nextRetryAt"];
    readonly retryNow: ReturnType<typeof useConnectionHealth>["retryNow"];
    readonly setRetrySeq: ReturnType<typeof useConnectionHealth>["setRetrySeq"];
    // End the session (server sign-out, then a fresh boot).
    readonly signOut: () => void;

    // Lifecycle surfaces.
    readonly conversationTitleFor: LifecycleSurfaces["conversationTitleFor"];
    readonly submitResourceRename: LifecycleSurfaces["submitResourceRename"];
    readonly submitConversationRename: LifecycleSurfaces["submitConversationRename"];
    readonly deleteResourceNow: LifecycleSurfaces["deleteResourceNow"];
    readonly undoDelete: LifecycleSurfaces["undoDelete"];
    readonly archiveResourceNow: LifecycleSurfaces["archiveResourceNow"];
    readonly unarchiveResourceNow: LifecycleSurfaces["unarchiveResourceNow"];
    readonly promoteFileNow: LifecycleSurfaces["promoteFileNow"];
    readonly deleteConversationNow: LifecycleSurfaces["deleteConversationNow"];
    readonly undoDeleteConversation: LifecycleSurfaces["undoDeleteConversation"];
    readonly saveSourceSummaryNow: LifecycleSurfaces["saveSourceSummaryNow"];
    readonly addSourceNote: LifecycleSurfaces["addSourceNote"];
    readonly deleteSourceNote: LifecycleSurfaces["deleteSourceNote"];
    readonly requestUploadForSource: LifecycleSurfaces["requestUploadForSource"];
    readonly downloadExportById: LifecycleSurfaces["downloadExportById"];
    readonly resourceOverflowItems: LifecycleSurfaces["resourceOverflowItems"];
    readonly beginRenameConversation: LifecycleSurfaces["beginRenameConversation"];
    readonly archiveConversationNow: LifecycleSurfaces["archiveConversationNow"];
    readonly unarchiveConversationNow: LifecycleSurfaces["unarchiveConversationNow"];
    readonly retrySave: LifecycleSurfaces["retrySave"];
    readonly packageNow: LifecycleSurfaces["packageNow"];
    readonly onExportStart: LifecycleSurfaces["onExportStart"];

    // Library-load + hero.
    readonly heroLayoutFor: HeroHostApi["heroLayoutFor"];

    // --- Loaded data + typed load errors ---
    readonly library: Library | null;
    readonly libraryError: ExternalError | null;
    // The projected auth status — the sign-in gate reads it (anonymous → the
    // sign-in surface). `null` while the boot probe is in flight.
    readonly authStatus: AuthStatus | null;
    readonly sourceDetail: LoadedSource | null;
    readonly sourceError: ExternalError | null;
    readonly exportDetail: LoadedExport | null;
    readonly exportError: ExternalError | null;
    readonly quality: MaterialQuality | null;
    readonly policy: Core.Policy.Evaluate.PolicyEvaluation | null;
    readonly policyCache: ReadonlyMap<
        string,
        Core.Policy.Evaluate.PolicyEvaluation
    >;
    readonly conversationSummaries: readonly RecentThread[];
    // The rail's view of the same list as a remote read (G6): loading and
    // failed are distinct from empty; `retryConversationList` re-reads it.
    readonly conversationList: ConversationList<RecentThread, ExternalError>;
    readonly retryConversationList: () => void;
    // The Search overlay's one server-shaped prop — the live Library +
    // conversation summaries projected into rankable entities, freshness stamped
    // against the host's `useNow` clock (searchProjection.ts). A pure projection
    // the host recomputes; SearchSurface ranks/filters it client-side.
    readonly searchResults: readonly SearchResult[];
    // The Library module, mounted in the shell's runtime.
    readonly libraryMount: LibraryMount;

    // --- Active-tab derivations the render tree keys on ---
    readonly activeRef: ResourceRef | null;
    readonly activeConversationId: string | null;
    readonly activeMaterialId: string | null;
    readonly activeTitle: string;
    readonly isSplit: boolean;
    readonly tabs: TabSet;
    readonly dock: ReturnType<typeof dockState>;
    readonly surface: ReturnType<typeof workspaceSurface>;
    readonly scope: ChatScope;
    readonly paneSubject: MaterialRef | null;
    readonly refFor: (id: ResourceId) => ResourceRef | undefined;
    readonly chatState: ChatState;
    readonly sessionFor: (conversationId: string | null) => ChatSession;
    // F76 — whether a DURABLE conversation's transcript is still loading
    // (durable ⟹ ≥1 message, so durable + empty = load in flight). The centre
    // reads this to show a loading state instead of flickering the hero.
    readonly conversationTranscriptPending: (conversationId: string) => boolean;

    // --- Composer + context vocabulary ---
    readonly composerCandidates: readonly ComposerResourceRef[];
    readonly composerMentions: readonly ComposerResourceRef[];
    readonly chatSlashCommands: readonly ComposerSlashCommand[];
    readonly selectableContexts: readonly SelectableContext[];
    readonly activeContextItems: readonly ComposerResourceRef[];
    readonly activeAmbientItems: readonly AmbientContextItem[];
    readonly sourceItems: readonly SourceRef[];
    // A nascent (not-yet-sent) conversation's pre-send grounding — held
    // client-side, keyed by conversation id (F32: the Files panel projects
    // this UNIONED with `activeContextItems` so an attached-not-yet-sent
    // source shows up consistently, not only the durable Context BC).
    readonly pendingGrounding: PendingGrounding;

    // --- Export dialog ---
    readonly exportFormats: readonly ExportFormat[];
    readonly exportState: ExportState;
    readonly setExportState: Dispatch<SetStateAction<ExportState>>;
    readonly exportReady: ExportReady | null;
    readonly setExportReady: Dispatch<SetStateAction<ExportReady | null>>;

    // --- Create dialog ---
    readonly templateCatalog: TemplateCatalog;
    readonly createState: MaterialCreateState;
    readonly createSeedTitle: string;
    readonly onCreateMaterial: (material: NewMaterial) => void;

    // --- History & Inspection overlay ---
    readonly historyView: HistoryView | null;
    readonly fileHistory: {
        readonly resourceKey: string;
        readonly view: FileHistoryView;
    } | null;
    readonly historyVersionId: string | null;
    readonly setHistoryVersionId: Dispatch<SetStateAction<string | null>>;
    readonly onRequestFileHistory: (resourceKey: string) => void;
    readonly onRestoreVersion: (resourceKey: string, marker: string) => void;

    // --- Finder overlay ---
    readonly finderDocs: readonly FinderDoc[];
    readonly finderLoading: boolean;

    // --- Lifecycle transient state (inline rename / delete confirmation) ---
    readonly renaming: Renaming | null;
    readonly setRenaming: Dispatch<SetStateAction<Renaming | null>>;
    readonly confirmDelete: ResourceRef | null;
    readonly setConfirmDelete: Dispatch<SetStateAction<ResourceRef | null>>;
    readonly confirmDeleteConversation: ConversationRailItem | null;
    readonly setConfirmDeleteConversation: Dispatch<
        SetStateAction<ConversationRailItem | null>
    >;

    // --- Ingest ---
    readonly ingest: readonly IngestFile[];
    readonly ingestHostRef: RefObject<IngestHostHandle>;
    readonly setIngest: Dispatch<SetStateAction<readonly IngestFile[]>>;
    // F83: the drop-time IngestId → conversationId linkage the composer
    // attachment projection reads, and the recorder IngestHost calls at
    // drop/pick time.
    readonly ingestConversations: ReadonlyMap<IngestId, string>;
    readonly recordIngestConversation: (
        ids: readonly IngestId[],
        conversationId: string | null,
    ) => void;
    readonly setLibrary: Dispatch<SetStateAction<Library | null>>;

    // --- Panels (rail/dock widths + drag-to-expand) ---
    readonly panelLayout: ReturnType<typeof readPanelLayout>;
    readonly dockDragPull: number | null;
    readonly setDockDragPull: Dispatch<SetStateAction<number | null>>;
    readonly railDragPull: number | null;
    readonly setRailDragPull: Dispatch<SetStateAction<number | null>>;
    readonly dockDragCfg: PanelDragConfig;
    readonly railDragCfg: PanelDragConfig;
    readonly resizeRail: (growPx: number) => void;
    readonly resetRail: () => void;
    readonly resizeDock: (growPx: number) => void;
    readonly resetDock: () => void;
    readonly endDockDrag: (pullPx: number) => void;
    readonly endRailDrag: (pullPx: number) => void;
    readonly revealRail: () => void;
    readonly closeSecondary: () => void;

    // --- Settings / identity ---
    readonly displayName: string;
    readonly onDisplayNameChange: (name: string) => void;
    // The editable workspace name (F56). Server-projected truth is
    // `Library.workspaceName` ("" = unnamed); this is the Settings→Account
    // field's draft, seeded + self-healed from every library read. The
    // change handler dispatches the `renameWorkspace` command.
    readonly workspaceName: string;
    readonly onWorkspaceNameChange: (name: string) => void;
    // Semantic-summary consent (IDD 94442b9d) — server-projected truth
    // (`Library.summary.consentEnabled`), with the device-local mirror as
    // the instant-paint cache / offline fallback, same split as
    // `displayName` above (see `app/summaryConsent.ts` /
    // `app/summaryProjection.ts`). This handler already forces a confirming
    // reread once the underlying write settles (`useAppShellHost`'s wrapper
    // over `useShellFoundation`'s raw dispatch).
    readonly summaryConsent: boolean;
    readonly onSummaryConsentChange: (consented: boolean) => void;

    // --- Platform + session facts ---
    readonly kbdPlatform: KbdPlatform;
    readonly isDesktopMac: boolean;
    readonly historicalPlayback: boolean;
    // The wall clock, ticked through the sanctioned `useNow` seam and threaded
    // down as a plain number so the render tree (Home's freshness projection,
    // HeroHost's recency label) stays Date-free.
    readonly nowMs: number;

    // --- Shell handlers the views emit intents through ---
    readonly showNotice: (n: HostNotice) => void;
    // The message stack's own intents (MessageLayer).
    readonly messageControls: MessageControls;
    readonly openOverlay: (overlay: OverlaySurface) => void;
    readonly closeOverlay: () => void;
    readonly summonSlot: (slot: SecondarySlot) => void;
    readonly gotoSettings: () => void;
    readonly closeSettings: () => void;
    // The Home overview singleton tab (dogfood F1) — the rail's Home icon opens
    // it; its own unguarded close intent mirrors Settings.
    readonly gotoHome: () => void;
    readonly closeHome: () => void;
    readonly gotoStudio: () => void;
    readonly studio: StudioCommands;
    // The Knowledge bank's list (the sidebar's Knowledge bank row).
    readonly gotoKnowledge: () => void;
    // The Search + Recent singleton tab (dogfood F6) — the rail's Search/Recent
    // doors open it; its own unguarded close intent mirrors Home. `kind`
    // optionally pre-selects a facet (chrome-shell rail categories / F33's
    // "open search filtered by kind" reuse); omitted ⇒ unfiltered.
    readonly gotoSearch: (kind?: SearchFacet) => void;
    readonly closeSearch: () => void;
    readonly navBack: () => void;
    readonly navForward: () => void;
    readonly mkConversationId: () => ConversationId;
    // The material-creation dialog with a fresh submission state (the
    // sidebar's "New material", the omnibar, the empty library).
    readonly openCreateDialog: () => void;
    readonly openFilePicker: () => void;
    readonly displayText: (e: ExternalError) => string;
    readonly turnFailureText: (failure: TurnFailure) => string;

    // What a notice's actions run (useNoticeView renders each message).
    readonly noticeDeps: NoticeViewDeps;
};

const AppShellContext = createContext<AppShellModel | null>(null);

export const AppShellProvider = AppShellContext.Provider;

export const useAppShell = (): AppShellModel => {
    const model = useContext(AppShellContext);
    if (model === null) {
        throw new Error(
            "AppShellModel not provided — AppShellHost must wrap its view tree in AppShellProvider",
        );
    }
    return model;
};
